// OneSignal Web SDK (v16) — integrazione nel sistema notifiche esistente.
// UN SOLO service worker: OneSignal viene importato dentro /sw.js (vedi public/sw.js),
// così non ci sono conflitti di scope e il file è sempre raggiungibile con il
// content-type corretto (lo stesso /sw.js della PWA, già funzionante in produzione).
// App ID pubblico (lato client); sovrascrivibile via REACT_APP_ONESIGNAL_APP_ID.

const APP_ID =
  process.env.REACT_APP_ONESIGNAL_APP_ID || "4ae42ccc-fd7d-40ad-9b8d-5a0de46c1ac4";

let initStarted = false;
let _initOk = false;
let _initResolve;
// Si risolve con true (init ok) oppure false (init fallito).
const _initReady = new Promise((res) => { _initResolve = res; });

// Utility: forza la risoluzione entro ms (evita await appesi all'infinito).
function withTimeout(promise, ms) {
  return Promise.race([
    Promise.resolve(promise),
    new Promise((_, rej) => setTimeout(() => rej(new Error("timeout")), ms)),
  ]);
}

export const oneSignalSupported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

// Coda ufficiale OneSignal: le funzioni vengono eseguite quando l'SDK è pronto.
function withOS() {
  return new Promise((resolve) => {
    if (typeof window === "undefined") return resolve(null);
    window.OneSignalDeferred = window.OneSignalDeferred || [];
    window.OneSignalDeferred.push((OneSignal) => resolve(OneSignal));
  });
}

// Attende l'esito dell'init: true (ok) | false (fallito) | "pending" (troppo lento).
function onceInit(timeoutMs = 30000) {
  return Promise.race([
    _initReady,
    new Promise((res) => setTimeout(() => res("pending"), timeoutMs)),
  ]);
}

export function initOneSignal() {
  if (typeof window === "undefined" || initStarted || !oneSignalSupported()) return;
  initStarted = true;
  window.OneSignalDeferred = window.OneSignalDeferred || [];
  window.OneSignalDeferred.push(async (OneSignal) => {
    try {
      await OneSignal.init({
        appId: APP_ID,
        allowLocalhostAsSecureOrigin: true,
        // Worker OneSignal DEDICATO servito dalla root, con scope separato dalla PWA.
        serviceWorkerPath: "OneSignalSDKWorker.js",
        serviceWorkerParam: { scope: "/onesignal/" },
        autoResubscribe: true,
      });
      _initOk = true;
    } catch (e) {
      // In preview (dominio diverso da quello configurato in OneSignal) l'init può
      // fallire: è atteso e non deve bloccare l'app.
      console.debug("OneSignal init note:", e?.message || e);
    } finally {
      try { _initResolve(_initOk); } catch (_) { /* noop */ }
    }
  });
}

function pushSub(OneSignal) {
  try { return OneSignal.User && OneSignal.User.PushSubscription; } catch { return null; }
}

// Attende che la subscription sia attiva: risolve con l'id se disponibile, oppure
// con "opted-in" se OneSignal segnala optedIn=true (l'id può propagarsi poco dopo).
function waitForSubscription(OneSignal, timeoutMs = 25000) {
  return new Promise((resolve) => {
    const start = Date.now();
    let onChange = null;
    const cleanup = () => {
      clearInterval(iv);
      try { if (onChange) pushSub(OneSignal).removeEventListener("change", onChange); } catch (_) { /* noop */ }
    };
    const check = () => {
      const ps = pushSub(OneSignal);
      if (ps && ps.id) { cleanup(); resolve({ id: ps.id, optedIn: ps.optedIn === true }); return true; }
      if (ps && ps.optedIn === true) { cleanup(); resolve({ id: null, optedIn: true }); return true; }
      return false;
    };
    if (check()) return;
    try {
      onChange = () => { check(); };
      pushSub(OneSignal).addEventListener("change", onChange);
    } catch (_) { /* noop */ }
    const iv = setInterval(() => {
      if (check()) return;
      if (Date.now() - start > timeoutMs) { cleanup(); resolve(null); }
    }, 600);
  });
}

// Collega il dispositivo all'utente loggato (External ID = id utente del gestionale).
// Dopo il login verifica che OneSignal.User.externalId corrisponda a String(externalId)
// e legge lo stato della PushSubscription (id, token, optedIn). Ritorna la diagnostica.
export async function oneSignalLogin(externalId, tags = {}) {
  if (!externalId) return null;
  const OneSignal = await withOS();
  if (!OneSignal) return null;
  const status = await onceInit();
  if (status !== true) {
    console.debug("OneSignal login skip: init status =", status);
    return null;
  }
  try {
    await OneSignal.login(String(externalId));
    const clean = {};
    Object.entries(tags).forEach(([k, v]) => { if (v != null) clean[k] = String(v); });
    if (Object.keys(clean).length && OneSignal.User && OneSignal.User.addTags) {
      await OneSignal.User.addTags(clean);
    }
  } catch (e) { console.debug("OneSignal login note", e?.message || e); }

  // Verifica associazione External ID e stato subscription (con piccola attesa).
  const diag = await oneSignalDiagnostics();
  const expected = String(externalId);
  diag.expected_external_id = expected;
  diag.external_id_match = diag.external_id === expected;
  if (!diag.external_id_match) {
    console.warn("[OneSignal] External ID NON allineato:", { expected, got: diag.external_id });
  }
  console.info("[OneSignal] login diagnostics:", diag);
  return diag;
}

// Restituisce lo stato client OneSignal utile alla diagnostica del test push.
// NB: non espone segreti; il token è troncato.
export async function oneSignalDiagnostics() {
  const out = { external_id: null, subscription_id: null, token: null,
                opted_in: null, permission: null };
  const OneSignal = await withOS();
  if (!OneSignal) return out;
  await onceInit();
  try {
    try { out.external_id = OneSignal.User?.externalId || (typeof OneSignal.User?.getExternalId === "function" ? await OneSignal.User.getExternalId() : null); } catch (_) { /* noop */ }
    const ps = pushSub(OneSignal);
    if (ps) {
      try { out.subscription_id = ps.id || null; } catch (_) { /* noop */ }
      try { const t = ps.token || null; out.token = t ? String(t).slice(0, 12) + "…" : null; } catch (_) { /* noop */ }
      try { out.opted_in = typeof ps.optedIn === "boolean" ? ps.optedIn : null; } catch (_) { /* noop */ }
    }
    try { out.permission = OneSignal.Notifications ? OneSignal.Notifications.permission : null; } catch (_) { /* noop */ }
  } catch (e) { console.debug("OneSignal diagnostics note", e?.message || e); }
  return out;
}

export async function oneSignalLogout() {
  const OneSignal = await withOS();
  if (!OneSignal) return;
  await onceInit();
  try { await OneSignal.logout(); } catch (e) { console.debug("OneSignal logout note", e?.message || e); }
}

export async function oneSignalIsEnabled() {
  const OneSignal = await withOS();
  if (!OneSignal) return false;
  await onceInit();
  try {
    const ps = pushSub(OneSignal);
    return !!(ps && ps.optedIn === true && OneSignal.Notifications &&
      OneSignal.Notifications.permission === true);
  } catch { return false; }
}

// Chiede il permesso, iscrive il dispositivo e attende la subscription reale.
// Ogni step ha un timeout: il pulsante non resta MAI in caricamento all'infinito.
export async function oneSignalEnable() {
  const OneSignal = await withOS();
  if (!OneSignal) throw new Error("Notifiche non disponibili su questo dispositivo");
  const status = await onceInit(30000);
  if (status === false) {
    throw new Error("Servizio notifiche non inizializzato: verifica che OneSignal Web sia configurato per questo dominio e riprova.");
  }
  if (status === "pending") {
    throw new Error("Inizializzazione notifiche ancora in corso: attendi qualche secondo e riprova.");
  }

  try { await withTimeout(OneSignal.Notifications.requestPermission(), 15000); }
  catch (e) { console.debug("requestPermission note", e?.message || e); }

  if (OneSignal.Notifications && OneSignal.Notifications.permission !== true) {
    throw new Error("Permesso notifiche negato dal browser");
  }

  try { await withTimeout(OneSignal.User.PushSubscription.optIn(), 15000); }
  catch (e) { console.debug("optIn note", e?.message || e); }

  // Attende la registrazione effettiva (id oppure optedIn). Niente loader infinito.
  let res = await waitForSubscription(OneSignal, 25000);

  // Se non risulta ancora iscritto, un secondo tentativo di opt-in (alcuni browser
  // creano la subscription solo dopo che il service worker è completamente pronto).
  if (!res) {
    try { await withTimeout(OneSignal.User.PushSubscription.optIn(), 10000); }
    catch (e) { console.debug("optIn retry note", e?.message || e); }
    res = await waitForSubscription(OneSignal, 15000);
  }

  const ps = pushSub(OneSignal);
  const optedIn = !!(ps && ps.optedIn === true);
  const permOk = !!(OneSignal.Notifications && OneSignal.Notifications.permission === true);
  // Successo se abbiamo l'id OPPURE il dispositivo risulta iscritto col permesso
  // concesso: il backend individua comunque la subscription via External ID / View
  // User API e consegna la notifica.
  if ((res && (res.id || res.optedIn)) || (optedIn && permOk)) {
    return true;
  }
  throw new Error("Iscrizione non completata: riprova. Se persiste, verifica il service worker in produzione.");
}

export async function oneSignalDisable() {
  const OneSignal = await withOS();
  if (!OneSignal) return true;
  await onceInit();
  try { await OneSignal.User.PushSubscription.optOut(); } catch (e) { console.debug("optOut note", e?.message || e); }
  return true;
}
